/* WebComponentAccordionGallery component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, h, Listen, Prop, State, Watch } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import AccordionGalleryTemplate from './templates/cwc-accordion-gallery.template';
import UtilBreakpoints from '@cewe-designsystem/util_breakpoints';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { SlideState, StateChangeDetail } from './states';

@Component({
  tag: 'cwc-accordion-gallery',
  styleUrl: './styles/cwc-accordion-gallery.scss',
  shadow: true,
})
export class AccordionGalleryComponent {
  /**
   * element
   */
  @Element() element: HTMLElement;

  @Prop({ attribute: 'default-slide', reflect: true, mutable: true })
  defaultSlide: number = 0;

  @Prop()
  alwaysOpen: boolean = false;

  @Prop()
  mobileBreakpoint: string = 'md';

  @State()
  openedSlides = [];

  /**
   * @constructor
   */
  constructor() {}

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    this.openFirstSlide();
  }

  @Listen('resize', { target: 'window', passive: true })
  openFirstSlide() {
    if (this.openedSlides.length > 0) {
      return;
    }

    const utilBreakpoints = new UtilBreakpoints();
    if (utilBreakpoints.breakpointDown(this.mobileBreakpoint) || this.alwaysOpen) {
      this.element.querySelectorAll('cwc-accordion-gallery-slide')[this.defaultSlide].open();
    } else {
      this.openedSlides = [];
    }
  }

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {}

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {}

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * render
   */
  render() {
    return <AccordionGalleryTemplate controller={this} />;
  }

  @Listen('on:accordion-gallery-slide:statechange')
  onSlideStateChange(event: CustomEvent<StateChangeDetail>) {
    // console.log('Received the custom stateChange event: ', event.detail);
    if (event.detail.newState == SlideState.Opened) {
      this.openedSlides = [...this.openedSlides, event.detail.element];
      this.activateSlide(event.detail.element, event.detail.backgroundSelector);
    }

    // only if the last closed event is detected
    if (event.detail.newState == SlideState.Closed) {
      this.openedSlides = this.openedSlides.filter((item) => item != event.detail.element);
    }

    // console.log('openedSlides', this.openedSlides);
  }

  @Watch('openedSlides')
  observeOpenedSlides(items) {
    if (items.length != 0) {
      this.element
        .querySelectorAll('cwc-accordion-gallery-slide')
        .forEach((slide) => slide.removeAttribute('allclosed'));
    } else {
      this.element
        .querySelectorAll('cwc-accordion-gallery-slide')
        .forEach((slide) => slide.setAttribute('allclosed', ''));
    }
  }

  activateSlide(activeSlide: HTMLElement, backgroundName: string) {
    // close others
    this.element.querySelectorAll('cwc-accordion-gallery-slide').forEach((slide) => {
      if (slide != activeSlide) {
        slide.close();
      }
    });

    // hide other backgrounds
    this.element.shadowRoot
      .querySelectorAll('backgrounds > slot:not([name="' + backgroundName + '"])')
      .forEach((slide) => slide.classList.remove('active'));

    // show active background
    this.element.shadowRoot.querySelector('backgrounds > slot[name="' + backgroundName + '"]').classList.add('active');
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(AccordionGalleryComponent.prototype, AbstractComponent.prototype);

export interface AccordionGalleryComponent extends AbstractComponent {}
